import { faCheckSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import "../../../css/common.css";
import { mapPeriods } from "../../../js/Mappers";

const ConstraintsTable = ({
  id,
  scheduleInfo,
  scheduleStructureData,
  teachers,
  courses,
  classrooms,
}) => {
  const [headerTitle, setHeaderTitle] = useState();
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const mappedPeriods = mapPeriods(
      scheduleInfo.schedule_type,
      scheduleStructureData
    );
    if (mappedPeriods && mappedPeriods.length > 0) {
      setPeriods(mappedPeriods);
    }
  }, [scheduleInfo, scheduleStructureData]);

  useEffect(() => {
    const constraintId = id;
    const title = mapConstraintsTableTitle(constraintId);
    const headers = mapConstraintsTableHeaders(constraintId, periods);
    const rows = mapConstraintsTableRows(
      constraintId,
      periods,
      teachers,
      courses,
      classrooms
    );
    setHeaderTitle(title);
    setHeaders(headers);
    setRows(rows);
    setLoading(false);
  }, [id, periods, teachers, courses, classrooms]);

  const mapConstraintsTableTitle = (constraintId) => {
    switch (constraintId) {
      case "teacher_availability":
      case "course_availability":
        return "Available Periods";
      default:
        return "";
    }
  };

  const mapConstraintsTableHeaders = (constraintId, periods) => {
    let headers = [];
    switch (constraintId) {
      case "teacher_availability":
      case "course_availability":
        if (periods && periods.length > 0) {
          headers = [
            constraintId === "teacher_availability" ? "Teacher" : "Course",
            ...periods.map((period) => period.period_name),
          ];
        }
        break;
      case "course_caps":
        headers = ["Course", "Max Number of Sections", "Max Students per Section"];
        break;
      case "course_lengths":
        headers = ["Course", "Semester", "Elective", "# Periods Spanned"];
        break;
      case "course_flags":
        headers = [
          "Course",
          "Is Lunch?",
          "Is Inclusion?",
          "Needs Lab?",
          "Swappable for Lab?",
          "Not in Schedule?",
        ];
        break;
      case "course_teachers":
        headers = ["Course", "Teachers"];
        break;
      case "classroom_settings":
        headers = ["Classroom", "Classroom Type", "Allows Multiple Sections", "Max Capacity"];
        break;
      case "course_classrooms":
      case "teacher_classrooms":
        headers = [constraintId === "course_classrooms" ? "Course" : "Teacher", "Classroom Restriction"];
        break;
      default:
    }
    return headers;
  };

  const mapConstraintsTableRows = (
    constraintId,
    periods,
    teachers,
    courses,
    classrooms
  ) => {
    let rows = [];
    switch (constraintId) {
      case "teacher_availability":
        if (teachers.length > 0 && periods.length > 0) {
          rows = teachers.map((teacher) => {
            let value = [];
            for (const period of periods) {
              teacher.period_restrictions.includes(period.period_id)
                ? value.push(false)
                : value.push(true);
            }
            return {
              name: `${teacher.name}, ${teacher.first_name}`,
              value,
            };
          });
        }
        break;
      case "course_availability":
        if (courses.length > 0 && periods.length > 0) {
          rows = courses.map((course) => {
            let value = [];
            for (const period of periods) {
              course.period_restrictions.includes(period.period_id)
                ? value.push(false)
                : value.push(true);
            }
            return {
              name: course.name,
              value,
            };
          });
        }
        break;
      case "course_caps":
        if (courses.length > 0) {
          rows = courses.map((course) => {
            return {
              name: course.name,
              value: [course.max_num_sections, course.max_class_size],
            };
          });
        }
        break;
      case "course_lengths":
        if (courses.length > 0) {
          rows = courses.map((course) => {
            return {
              name: course.name,
              value: [
                course.semester,
                course.elective === "0" ? "Non-Elective" : "Elective",
                course.num_periods_spanned,
              ],
            };
          });
        }
        break;
      case "course_flags":
        if (courses.length > 0) {
          rows = courses.map((course) => {
            return {
              name: course.name,
              value: [
                course.is_lunch === "1",
                course.allow_icr_students === "1",
                course.is_separate_lab === "1",
                course.is_swappable_for_lab === "1",
                course.is_outside_course === "1",
              ],
            };
          });
        }
        break;
      case "course_teachers":
        if (courses.length > 0) {
          rows = courses.map((course) => {
            let courseTeachers = [];
            if (
              course.teachers_can_teach &&
              course.teachers_can_teach.length > 0
            ) {
              for (const teacherCanTeach of course.teachers_can_teach) {
                for (const teacher of teachers) {
                  if (teacherCanTeach.teacher_id === teacher.teacher_id) {
                    const type =
                      teacherCanTeach.type === "1" ? "Primary" : "Back Up";
                    courseTeachers.push(
                      `${teacher.name}, ${teacher.first_name} - ${type}`
                    );
                  }
                }
              }
            }
            return {
              name: course.name,
              value: courseTeachers.length > 0 ? courseTeachers : ["-"],
            };
          });
        }
        break;
      case "classroom_settings":
        if (classrooms.length > 0) {
          rows = classrooms.map((classroom) => {
            return {
              name: `Room ${classroom.classroom_name}`,
              value: [classroom.classroom_type, classroom.multi_class === "1" ? "Y" : "N", classroom.max_capacity],
            };
          });
        }
        break;
      case "course_classrooms":
        rows = courses.map((course) => {
          let courseClassrooms = [];
          for (const classroom of classrooms) {
            if (
              course.classroom_restrictions.includes(classroom.classroom_id)
            ) {
              courseClassrooms.push(`Room ${classroom.classroom_name}`);
            }
          }
          return {
            name: course.name,
            value: courseClassrooms.length > 0 ? courseClassrooms : ["-"],
          };
        });
        break;
      case "teacher_classrooms":
        rows = teachers.map((teacher) => {
          let teacherClassrooms = [];
          for (const classroom of classrooms) {
            if (
              teacher.classroom_restrictions.includes(classroom.classroom_id)
            ) {
              teacherClassrooms.push(`Room ${classroom.classroom_name}`);
            }
          }
          return {
            name: `${teacher.name}, ${teacher.first_name}`,
            value: teacherClassrooms.length > 0 ? teacherClassrooms : ["-"],
          };
        });
        break;
      default:
    }
    return rows;
  };

  return (
    <div className="const-table-container">
      {loading && (
        <div
          className="loader"
          style={{
            width: "35px",
            height: "35px",
            display: "block",
            margin: "20px auto",
          }}
        ></div>
      )}

      {!loading && headers.length > 0 && rows.length > 0 && (
        <div className="">
          {headerTitle && (
            <div
              className="const-table-header-row"
              style={{
                gridTemplateColumns: "300px 1fr",
                margin: "0px 0px 5px",
              }}
            >
              <div></div>
              <div
                className="dark-blue"
                style={{
                  textAlign: "center",
                  padding: "5px",
                  borderRadius: "5px",
                }}
              >
                {headerTitle}
              </div>
            </div>
          )}

          {headers && headers.length > 0 && (
            <div
              className="const-table-header-row"
              style={{
                gridTemplateColumns: `300px repeat(${headers.length - 1}, 1fr)`,
                gridTemplateRows: "25px",
              }}
            >
              {headers.map((header, index) => (
                <div key={index} className="const-table-col">
                  <div
                    key={`heading-${index}`}
                    className={`const-table-heading ${
                      index === 0 ? "const-table-row-name" : ""
                    }`}
                  >
                    {header}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!loading && headers.length > 0 && rows.length > 0 && (
        <div
          className="const-table-row"
          style={{
            gridTemplateColumns: `300px repeat(${headers.length - 1}, 1fr)`,
            gridTemplateRows: `repeat(${rows.length}, minmax(35px, auto))`,
          }}
        >
          {rows.map((row, index) => {
            const rowClass = index % 2 === 0 ? "const-table-row-color" : "";
            return (
              <div className="const-table-row-wrapper" key={index}>
                <div className={`const-table-row-name ${rowClass}`} key={index}>
                  <div>{row.name}</div>
                </div>
                {row.value.map((value, index) => {
                  if (
                    [
                      "course_teachers",
                      "course_classrooms",
                      "teacher_classrooms",
                    ].includes(id)
                  ) {
                    return (
                      <div key={index} className={`const-table-column-2 ${rowClass}`}>
                        {value}
                      </div>
                    );
                  } else if (typeof value === "boolean") {
                    if (value) {
                      return (
                        <div key={index} className={`const-table-checkmark ${rowClass}`}>
                          <FontAwesomeIcon
                            className="fas-checkbox-unchecked"
                            icon={faCheckSquare}
                          />
                        </div>
                      );
                    } else {
                      return (
                        <div className={`const-table-row ${rowClass}`} key={index}>
                          --
                        </div>
                      );
                    }
                  }
                  return (
                    <div className={`const-table-row ${rowClass}`} key={index}>
                      {value === "None" || !value ? "--" : value}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ConstraintsTable;
